import { dec, lineAmount, lineQtyPcs, markupPctFromPrices, wholesaleFromMarkup } from '@app/shared';
import type { CategoryDto } from '@/entities/category';
import type { DocumentDto, DocumentLineDto, DocumentLineInput, DuplicateTemplateDto, ReturnTemplateDto, WriteOffReason } from '@/entities/document';

export type EditorDocumentType = 'PURCHASE' | 'SALE' | 'RETURN_IN' | 'RETURN_OUT' | 'WRITE_OFF' | 'INVENTORY';

export interface EditorLine {
  key: string;
  lineNo: number;
  categoryId?: string;
  categoryName?: string;
  categoryPcsPerBox?: number | null;
  stock?: { sealedBoxes: number; loosePcs: number; qtyTotal: number };
  boxes: number | null;
  pcsPerBox: number | null;
  unitPrice: string | null;
  wholesalePrice: string | null;
  markupPct: string | null;
  lastEdited: 'wholesale' | 'markup';
  isNew: boolean;
  isPromo: boolean;
  expiryDate: string | null;
  unitCost: string | null;
  costAmount: string | null;
  costIsProvisional: boolean;
  /** RETURN_IN/RETURN_OUT (§11.2/§11.3): строка-основание и предзаполненный контекст. */
  baseLineId?: string;
  soldQty?: number;
  alreadyReturnedQty?: number;
  qtyOverBase?: number;
  /** WRITE_OFF (§11.4). */
  reason?: WriteOffReason | null;
  batchId?: string | null;
  /** INVENTORY (§6.1 [ИЗМ]): факт — целые коробки/россыпь. */
  countedBoxes?: number | null;
  countedLoose?: number | null;
}

let keySeq = 0;
function nextKey(): string {
  keySeq += 1;
  return `l${keySeq}-${Date.now()}`;
}

export function emptyLine(lineNo: number): EditorLine {
  return {
    key: nextKey(),
    lineNo,
    boxes: null,
    pcsPerBox: null,
    unitPrice: null,
    wholesalePrice: null,
    markupPct: null,
    lastEdited: 'wholesale',
    isNew: false,
    isPromo: false,
    expiryDate: null,
    unitCost: null,
    costAmount: null,
    costIsProvisional: false,
    reason: null,
    batchId: null,
    countedBoxes: null,
    countedLoose: null,
  };
}

export function fromDocumentLine(dto: DocumentLineDto): EditorLine {
  return {
    key: nextKey(),
    lineNo: dto.lineNo,
    categoryId: dto.categoryId ?? undefined,
    categoryName: dto.category?.name,
    boxes: dto.boxes,
    pcsPerBox: dto.pcsPerBox,
    unitPrice: dto.unitPrice,
    wholesalePrice: dto.wholesalePrice,
    markupPct: dto.markupPct,
    lastEdited: 'wholesale',
    isNew: dto.isNew,
    isPromo: dto.isPromo,
    expiryDate: dto.expiryDate,
    unitCost: dto.unitCost,
    costAmount: dto.costAmount,
    costIsProvisional: dto.costIsProvisional,
    baseLineId: dto.baseLineId ?? undefined,
    qtyOverBase: dto.qtyOverBase,
    reason: dto.reason,
    batchId: dto.batchId,
    countedBoxes: dto.countedBoxes,
    countedLoose: dto.countedLoose,
  };
}

/** §11.2/§11.3 «Создать возврат»: шаблон из проведённой продажи/прихода — ничего не сохраняет. */
export function fromReturnTemplate(template: ReturnTemplateDto): EditorLine[] {
  if (template.lines.length === 0) return [emptyLine(1)];
  return template.lines.map((l) => ({
    key: nextKey(),
    lineNo: l.lineNo,
    categoryId: l.categoryId ?? undefined,
    boxes: l.boxes,
    pcsPerBox: l.pcsPerBox,
    unitPrice: l.unitPrice,
    wholesalePrice: null,
    markupPct: null,
    lastEdited: 'wholesale',
    isNew: false,
    isPromo: false,
    expiryDate: null,
    unitCost: null,
    costAmount: null,
    costIsProvisional: false,
    baseLineId: l.baseLineId,
    soldQty: l.soldQty,
    alreadyReturnedQty: l.alreadyReturnedQty,
    reason: null,
    batchId: null,
  }));
}

/** §12.6 «Дублировать»: шаблон из любого документа — ничего не сохраняет. Строка с архивной
 * категорией переносится как есть; сервер заблокирует проведение (§8.1 CATEGORY_ARCHIVED), пока
 * категорию не заменят вручную — тот же путь, что и обычная правка строки с архивной категорией. */
export function fromDuplicateTemplate(template: DuplicateTemplateDto): EditorLine[] {
  if (template.lines.length === 0) return [emptyLine(1)];
  return template.lines.map((l) => ({
    key: nextKey(),
    lineNo: l.lineNo,
    categoryId: l.categoryId ?? undefined,
    boxes: l.boxes,
    pcsPerBox: l.pcsPerBox,
    unitPrice: l.unitPrice,
    wholesalePrice: l.wholesalePrice,
    markupPct: l.markupPct,
    lastEdited: 'wholesale',
    isNew: l.isNew,
    isPromo: l.isPromo,
    expiryDate: null,
    unitCost: null,
    costAmount: null,
    costIsProvisional: false,
    reason: null,
    batchId: null,
  }));
}

/** Количество и сумма — производные, всегда пересчитываются (§4, §5). */
export function qtyOf(line: EditorLine): number {
  return lineQtyPcs(line.boxes, line.pcsPerBox);
}

export function amountOf(line: EditorLine): string {
  return lineAmount(qtyOf(line), line.unitPrice ?? undefined).toFixed(2);
}

/** §4: «%» и «опт» связаны, источник — поле, которое менялось последним. */
export function recalcMarkup(line: EditorLine): EditorLine {
  if (!line.unitPrice) return line;
  const cost = dec(line.unitPrice);
  if (cost.isZero()) return line;
  if (line.lastEdited === 'markup' && line.markupPct) {
    return { ...line, wholesalePrice: wholesaleFromMarkup(cost, line.markupPct).toFixed(2) };
  }
  if (line.wholesalePrice) {
    const pct = markupPctFromPrices(cost, line.wholesalePrice);
    return { ...line, markupPct: pct ? pct.toFixed(2) : null };
  }
  return line;
}

/** §5: при выборе товара — P=N, коробок=1, цена=опт. категории (SALE/RETURN_IN — цена сразу). */
export function applyCategoryDefaults(line: EditorLine, category: CategoryDto, type: EditorDocumentType): EditorLine {
  const base: EditorLine = {
    ...line,
    categoryId: category.id,
    categoryName: category.name,
    categoryPcsPerBox: category.pcsPerBox,
    stock: category.stock ?? undefined,
    pcsPerBox: category.pcsPerBox,
  };
  if (type === 'SALE' || type === 'RETURN_IN') {
    return { ...base, boxes: base.boxes ?? 1, unitPrice: base.unitPrice ?? category.wholesalePrice };
  }
  if (type === 'WRITE_OFF' || type === 'INVENTORY') {
    return { ...base, boxes: base.boxes ?? 1 };
  }
  return { ...base, wholesalePrice: base.wholesalePrice ?? category.wholesalePrice };
}

/** §6.1 [ПРЕДЛ] «Заполнить по остаткам»: факт = текущий учёт, для строк с выбранным товаром. */
export function fillFromStock(lines: EditorLine[]): EditorLine[] {
  return lines.map((l) =>
    l.categoryId && l.stock ? { ...l, countedBoxes: l.stock.sealedBoxes, countedLoose: l.stock.loosePcs } : l,
  );
}

/** INVENTORY (§6.1 [ИЗМ]): «Разница, шт» = факт − учёт, знаковая. */
export function inventoryDiff(line: EditorLine): number | null {
  if (line.countedBoxes == null || line.countedLoose == null || !line.stock || !line.pcsPerBox) return null;
  const factTotal = line.countedBoxes * line.pcsPerBox + line.countedLoose;
  return factTotal - line.stock.qtyTotal;
}

export function totals(lines: EditorLine[]): { positions: number; pcs: number; amount: string } {
  const withCategory = lines.filter((l) => l.categoryId);
  const pcs = withCategory.reduce((sum, l) => sum + qtyOf(l), 0);
  const amount = withCategory.reduce((sum, l) => sum.plus(dec(amountOf(l))), dec(0));
  return { positions: withCategory.length, pcs, amount: amount.toFixed(2) };
}

export function toLineInput(line: EditorLine, type: EditorDocumentType): DocumentLineInput {
  if (type === 'INVENTORY') {
    return {
      lineNo: line.lineNo,
      categoryId: line.categoryId,
      countedBoxes: line.countedBoxes ?? undefined,
      countedLoose: line.countedLoose ?? undefined,
      isNew: line.isNew,
      isPromo: line.isPromo,
    };
  }
  return {
    lineNo: line.lineNo,
    categoryId: line.categoryId,
    boxes: line.boxes ?? undefined,
    pcsPerBox: line.pcsPerBox ?? undefined,
    unitPrice: type === 'WRITE_OFF' ? undefined : (line.unitPrice ?? undefined),
    ...(type === 'PURCHASE'
      ? {
          wholesalePrice: line.wholesalePrice ?? undefined,
          markupPct: line.markupPct ?? undefined,
          lastEdited: line.lastEdited,
          expiryDate: line.expiryDate ?? undefined,
        }
      : {}),
    ...(type === 'RETURN_IN' || type === 'RETURN_OUT' ? { baseLineId: line.baseLineId } : {}),
    ...(type === 'WRITE_OFF' ? { reason: line.reason ?? undefined, batchId: line.batchId ?? undefined } : {}),
    isNew: line.isNew,
    isPromo: line.isPromo,
  };
}

/** §8.1: WRITE_OFF не требует контрагента/цены, но требует причину; остальные типы — как раньше. */
export function isDocumentComplete(type: EditorDocumentType, header: { docDate: string; counterpartyId?: string }, lines: EditorLine[]): boolean {
  if (!header.docDate) return false;
  if (type !== 'WRITE_OFF' && type !== 'INVENTORY' && !header.counterpartyId) return false;
  const withCategory = lines.filter((l) => l.categoryId);
  if (withCategory.length === 0) return false;
  if (type === 'INVENTORY') {
    return withCategory.every((l) => l.countedBoxes != null && l.countedBoxes >= 0 && l.countedLoose != null && l.countedLoose >= 0);
  }
  return withCategory.every((l) => {
    if (!l.categoryId || !l.boxes || l.boxes < 1 || !l.pcsPerBox || l.pcsPerBox < 1) return false;
    if (type === 'WRITE_OFF') return !!l.reason;
    return l.unitPrice !== null;
  });
}

/** `newLabel` уже согласован по роду с «Новый/Новая/Новое» — не собирается шаблоном. */
const TYPE_NAME: Record<EditorDocumentType, { newLabel: string; prefix: string }> = {
  PURCHASE: { newLabel: 'Новый приход', prefix: 'Приход' },
  SALE: { newLabel: 'Новая продажа', prefix: 'Продажа' },
  RETURN_IN: { newLabel: 'Новый возврат от клиента', prefix: 'Возврат от клиента' },
  RETURN_OUT: { newLabel: 'Новый возврат поставщику', prefix: 'Возврат поставщику' },
  WRITE_OFF: { newLabel: 'Новое списание', prefix: 'Списание' },
  INVENTORY: { newLabel: 'Новая инвентаризация', prefix: 'Инвентаризация' },
};

export function windowTitle(type: EditorDocumentType, doc: DocumentDto | undefined, isNew: boolean, copyOfNumber?: string): string {
  const { newLabel, prefix } = TYPE_NAME[type];
  if (copyOfNumber) return `${newLabel} (копия ${copyOfNumber})`;
  if (isNew || !doc) return newLabel;
  const num = `${doc.number}/${doc.numberYear}`;
  const statusLabel = doc.status === 'POSTED' ? ' (проведён)' : doc.status === 'CANCELLED' ? ' (отменён)' : '';
  return `${prefix} №${num}${statusLabel}`;
}
